/* global Office console */

/**
 * Gets the current user's email address (who would be the organizer in compose mode)
 * @returns Promise that resolves to current user's email or null
 */
async function getCurrentUserEmail(): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      // Get current user's email from userProfile
      if (Office.context.mailbox.userProfile?.emailAddress) {
        resolve(Office.context.mailbox.userProfile.emailAddress)
        return
      }

      resolve(null)
    } catch (error) {
      console.log("Error getting current user email: " + error)
      resolve(null)
    }
  })
}

/**
 * Filters out the current user from an attendee list to prevent organizer duplication
 * @param attendees Array of attendees to filter
 * @param userEmail The current user's email address
 * @returns Filtered attendees without the current user
 */
function filterOutCurrentUser(
  attendees: Office.EmailAddressDetails[],
  userEmail: string | null
): Office.EmailAddressDetails[] {
  if (!userEmail) return attendees

  const userEmailLower = userEmail.toLowerCase()
  return attendees.filter(attendee =>
    attendee?.emailAddress?.toLowerCase() !== userEmailLower
  )
}

/**
 * Safely extracts the lowercased identifiers (email address and display name)
 * that can be used to match an attendee against a configured visit-type address.
 *
 * Office.js resolves distribution lists asynchronously and, depending on the
 * Outlook client, returns them with an empty or undefined `emailAddress` and the
 * resolved list name in `displayName` (with `recipientType` `distributionList`)
 * rather than the configured SMTP address. Because the add-in sets both
 * `emailAddress` and `displayName` to the configured address when inserting an
 * attendee (see {@link createEmailAddressDetails}), inspecting both fields lets us
 * still recognise a previously added visit-type address after Outlook has resolved
 * it. Guarding against missing values is equally important: calling `.toLowerCase()`
 * on an undefined `emailAddress` would throw inside the async Office.js callback,
 * leaving the surrounding promise unsettled and silently breaking every subsequent
 * add/remove operation.
 *
 * @param attendee The attendee/recipient returned by Office.js
 * @returns Lowercased, non-empty identifiers for the attendee
 */
function getAttendeeIdentifiers(attendee: Office.EmailAddressDetails | undefined): string[] {
  return [attendee?.emailAddress, attendee?.displayName]
    .filter((value): value is string => typeof value === "string" && value.length > 0)
    .map(value => value.toLowerCase())
}

/**
 * Determines whether an attendee corresponds to one of the configured visit-type
 * addresses. Matching on either the resolved email address or the display name
 * keeps removal working even when Outlook has resolved a distribution list to a
 * different representation than the address the add-in originally inserted.
 *
 * @param attendee The attendee/recipient returned by Office.js
 * @param visitTypeAddressesLower Set of configured visit-type addresses (lowercased)
 * @returns True if the attendee is a managed visit-type address
 */
function isVisitTypeAttendee(
  attendee: Office.EmailAddressDetails,
  visitTypeAddressesLower: Set<string>
): boolean {
  return getAttendeeIdentifiers(attendee).some(identifier => visitTypeAddressesLower.has(identifier))
}

/**
 * Creates a proper EmailAddressDetails object for new attendees
 * @param emailAddress The email address
 * @returns EmailAddressDetails object
 */
function createEmailAddressDetails(emailAddress: string): Office.EmailAddressDetails {
  return {
    emailAddress: emailAddress,
    displayName: emailAddress,
    appointmentResponse: Office.MailboxEnums.ResponseType.None,
    recipientType: Office.MailboxEnums.RecipientType.Other
  }
}

export async function insertText(text: string) {
  // Write text to the cursor point in the compose surface.
  try {
    Office.context.mailbox.item?.body.setSelectedDataAsync(
      text,
      { coercionType: Office.CoercionType.Text },
      (asyncResult: Office.AsyncResult<void>) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          throw asyncResult.error.message
        }
      }
    )
  } catch (error) {
    console.log("Error: " + error)
  }
}

export async function removeVisitTypeEmails(visitTypeAddresses: string[], attendeeType: 'required' | 'optional' = 'required'): Promise<void> {
  // Remove visit type emails while preserving external attendees
  const item = Office.context.mailbox.item
  if (!item) {
    throw new Error("No mail item found")
  }

  // Guard against missing addresses so a single bad entry cannot throw and
  // break workspace switching for every workspace.
  const visitTypeAddressesLower = new Set(
    visitTypeAddresses
      .filter((addr): addr is string => typeof addr === "string" && addr.length > 0)
      .map(addr => addr.toLowerCase())
  )
  const currentUserEmail = await getCurrentUserEmail()

  return new Promise((resolve, reject) => {
    if (item.itemType === Office.MailboxEnums.ItemType.Appointment) {
      // Handle calendar appointments/meetings
      const targetProperty = attendeeType === 'required' ? item.requiredAttendees : item.optionalAttendees

      targetProperty.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          const errorMessage = `Error getting current ${attendeeType} attendees: ` + result.error.message
          console.log(errorMessage)
          reject(new Error(errorMessage))
          return
        }

        const currentAttendees = result.value || []

        // Filter out visit type addresses but keep external attendees.
        // Matches on both the email address and display name so distribution
        // lists resolved by Outlook are still recognised and removed.
        let filteredAttendees = currentAttendees.filter(attendee =>
          !isVisitTypeAttendee(attendee, visitTypeAddressesLower)
        )

        // Always filter out current user to prevent organizer duplication
        if (currentUserEmail) {
          filteredAttendees = filterOutCurrentUser(filteredAttendees, currentUserEmail)
        }

        // Only update if there's a change
        const hasChanges = JSON.stringify(filteredAttendees) !== JSON.stringify(currentAttendees)
        if (hasChanges) {
          targetProperty.setAsync(filteredAttendees, (setResult: Office.AsyncResult<void>) => {
            if (setResult.status === Office.AsyncResultStatus.Failed) {
              const errorMessage = `Error removing visit type emails from ${attendeeType} attendees: ` + setResult.error.message
              console.log(errorMessage)
              reject(new Error(errorMessage))
            } else {
              console.log(`Successfully removed visit type emails from ${attendeeType} attendees`)
              resolve()
            }
          })
        } else {
          resolve() // No changes needed, resolve immediately
        }
      })
    } else if (item.itemType === Office.MailboxEnums.ItemType.Message) {
      // Handle mail messages
      item.to.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          const errorMessage = "Error getting current recipients: " + result.error.message
          console.log(errorMessage)
          reject(new Error(errorMessage))
          return
        }

        const currentRecipients = result.value || []

        // Filter out visit type addresses but keep external recipients.
        // Matches on both the email address and display name so distribution
        // lists resolved by Outlook are still recognised and removed.
        const filteredRecipients = currentRecipients.filter(recipient =>
          !isVisitTypeAttendee(recipient, visitTypeAddressesLower)
        )

        // Only update if there's a change
        const hasChanges = filteredRecipients.length < currentRecipients.length
        if (hasChanges) {
          item.to.setAsync(filteredRecipients, (setResult: Office.AsyncResult<void>) => {
            if (setResult.status === Office.AsyncResultStatus.Failed) {
              const errorMessage = "Error removing visit type emails from To field: " + setResult.error.message
              console.log(errorMessage)
              reject(new Error(errorMessage))
            } else {
              console.log("Successfully removed visit type emails from To field")
              resolve()
            }
          })
        } else {
          resolve() // No changes needed, resolve immediately
        }
      })
    } else {
      const errorMessage = "Unsupported item type: " + item.itemType
      console.log(errorMessage)
      reject(new Error(errorMessage))
    }
  })
}

export async function addEmailToItem(emailAddress: string, attendeeType: 'required' | 'optional' = 'required'): Promise<void> {
  // Universal function to add emails to both mail items and appointments
  const item = Office.context.mailbox.item
  if (!item) {
    throw new Error("No mail item found")
  }

  const currentUserEmail = await getCurrentUserEmail()

  return new Promise((resolve, reject) => {
    if (item.itemType === Office.MailboxEnums.ItemType.Appointment) {
      // Handle calendar appointments/meetings
      const targetProperty = attendeeType === 'required' ? item.requiredAttendees : item.optionalAttendees

      targetProperty.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          const errorMessage = `Error getting current ${attendeeType} attendees: ` + result.error.message
          console.log(errorMessage)
          reject(new Error(errorMessage))
          return
        }

        let currentAttendees = result.value || []

        // Always filter out current user before processing to prevent organizer duplication
        if (currentUserEmail) {
          currentAttendees = filterOutCurrentUser(currentAttendees, currentUserEmail)
        }

        // Check if email already exists (guarding against attendees such as
        // resolved distribution lists that may expose no email address)
        const newEmailAddressLower = emailAddress.toLowerCase()
        const emailExists = currentAttendees.some(attendee =>
          getAttendeeIdentifiers(attendee).includes(newEmailAddressLower)
        )

        if (emailExists) {
          console.log(`Email address already exists in ${attendeeType} attendees: ` + emailAddress)
          resolve() // Email already exists, consider this a success
          return
        }

        const newAttendee = createEmailAddressDetails(emailAddress)

        let updatedAttendees = [...currentAttendees, newAttendee]

        targetProperty.setAsync(updatedAttendees, (setResult: Office.AsyncResult<void>) => {
          if (setResult.status === Office.AsyncResultStatus.Failed) {
            const errorMessage = `Error setting ${attendeeType} attendees: ` + setResult.error.message
            console.log(errorMessage)
            reject(new Error(errorMessage))
          } else {
            console.log(`Successfully added email to ${attendeeType} attendees: ` + emailAddress)
            resolve()
          }
        })
      })
    } else if (item.itemType === Office.MailboxEnums.ItemType.Message) {
      // Handle mail messages
      item.to.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          const errorMessage = "Error getting current recipients: " + result.error.message
          console.log(errorMessage)
          reject(new Error(errorMessage))
          return
        }

        const currentRecipients = result.value || []

        // Check if email already exists (guarding against recipients such as
        // resolved distribution lists that may expose no email address)
        const newEmailAddressLower = emailAddress.toLowerCase()
        const emailExists = currentRecipients.some(recipient =>
          getAttendeeIdentifiers(recipient).includes(newEmailAddressLower)
        )

        if (emailExists) {
          console.log("Email address already exists in To field: " + emailAddress)
          resolve() // Email already exists, consider this a success
          return
        }

        const newRecipient = createEmailAddressDetails(emailAddress)

        const updatedRecipients = [...currentRecipients, newRecipient]

        item.to.setAsync(updatedRecipients, (setResult: Office.AsyncResult<void>) => {
          if (setResult.status === Office.AsyncResultStatus.Failed) {
            const errorMessage = "Error setting recipients: " + setResult.error.message
            console.log(errorMessage)
            reject(new Error(errorMessage))
          } else {
            console.log("Successfully added email to To field: " + emailAddress)
            resolve()
          }
        })
      })
    } else {
      const errorMessage = "Unsupported item type: " + item.itemType
      console.log(errorMessage)
      reject(new Error(errorMessage))
    }
  })
}

